import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import api from '../utils/apiClient';

// Ensure API_URL has a default value
const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';

interface User {
  id: string;
  email: string;
  name: string;
  token?: string;
  refresh_token?: string;
}

interface AuthContextType {
  isAuthenticated: boolean;
  user?: User | null;
  login: (provider: 'google' | 'facebook' | 'microsoft') => Promise<void>;
  logout: () => Promise<void>;
  handleTokens: (tokens: { access: string, refresh: string }) => void;
  refreshAccessToken: () => Promise<boolean>;
  fetchUserProfile: () => Promise<User | null>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Token refresh state management
type TokenRefreshState = {
  inProgress: boolean;
  promise: Promise<boolean> | null;
  retryCount: number;
  lastAttempt: number;
};

// Global singleton for token refresh state
const tokenRefreshState: TokenRefreshState = {
  inProgress: false,
  promise: null,
  retryCount: 0,
  lastAttempt: 0
};

// Add a cache for user profile data
interface ProfileCache {
  data: User | null;
  timestamp: number;
}

const profileCache: ProfileCache = {
  data: null,
  timestamp: 0
};

// Standalone token refresh function that can be imported directly
// This will be used by our API client
export const refreshAccessToken = async (): Promise<boolean> => {
  const userJson = localStorage.getItem('user');
  if (!userJson) {
    console.log('❌ No user found in localStorage, cannot refresh token');
    return false;
  }

  const user = JSON.parse(userJson);
  if (!user?.refresh_token) {
    console.log('❌ No refresh token available');
    return false;
  }

  // If there's already a refresh in progress, return the existing promise
  if (tokenRefreshState.inProgress && tokenRefreshState.promise) {
    console.log('🔄 Token refresh already in progress, reusing promise');
    return tokenRefreshState.promise;
  }

  // Set inProgress flag and create new promise
  tokenRefreshState.inProgress = true;
  tokenRefreshState.lastAttempt = Date.now();
  tokenRefreshState.retryCount++;
  
  tokenRefreshState.promise = new Promise<boolean>(async (resolve) => {
    try {
      console.log('📤 Sending refresh token to API...');
      const response = await fetch(`${API_URL}/auth/refresh/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          refresh: user.refresh_token
        })
      });
      
      if (response.ok) {
        const data = await response.json();
        // Update only the access token, keep the same refresh token
        const updatedUser = {
          ...user,
          token: data.access
        };
        localStorage.setItem('user', JSON.stringify(updatedUser));
        // We can't call setUser here since this might be called outside the AuthProvider
        console.log('✅ Access token refreshed successfully');
        resolve(true);
      } else {
        // If refresh fails, we'll need to re-authenticate
        const errorData = await response.json().catch(() => ({ error: 'Unknown error' }));
        console.log('❌ Token refresh failed:', errorData.error || response.status);
        resolve(false);
      }
    } catch (error) {
      console.error('❌ Error refreshing token:', error);
      resolve(false);
    } finally {
      // Reset state after a short delay to prevent immediate retry floods
      setTimeout(() => {
        tokenRefreshState.inProgress = false;
        tokenRefreshState.promise = null;
      }, 100);
    }
  });
  
  return tokenRefreshState.promise;
};

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const navigate = useNavigate();
  const location = useLocation();

  // Function to handle tokens and set user
  const handleTokens = useCallback((tokens: { access: string, refresh: string }) => {
    // Create a temporary user object with the tokens
    const tempUser = {
      id: 'temp-id',
      email: 'user@example.com', // Will be replaced when user data is fetched
      name: 'User',
      token: tokens.access,
      refresh_token: tokens.refresh,
    };
    
    // Store the temporary user in localStorage and state
    localStorage.setItem('user', JSON.stringify(tempUser));
    setUser(tempUser);
    
    // Clear profile cache when tokens change
    profileCache.data = null;
    profileCache.timestamp = 0;
    
    // Fetch the actual user profile to get the correct ID and other details
    // We'll do this asynchronously to not block the auth flow
    setTimeout(async () => {
      try {
        const userProfile = await api.get<User>('/auth/profile/');
        if (userProfile) {
          // Create an updated user object with the correct ID and other details from the profile
          // but keep the tokens from the temp user
          const updatedUser = {
            ...userProfile,
            token: tokens.access,
            refresh_token: tokens.refresh,
          };
          
          // Update localStorage and state with the correct user data
          localStorage.setItem('user', JSON.stringify(updatedUser));
          setUser(updatedUser);
          
          console.log('Updated user profile with correct ID:', updatedUser.id);
        }
      } catch (error) {
        console.error('Error fetching user profile after login:', error);
      }
    }, 500); // Small delay to ensure tokens are properly set
    
    // Redirect to the saved redirect URL or app page
    const redirectUrl = localStorage.getItem('auth_redirect') || '/app';
    localStorage.removeItem('auth_redirect');
    navigate(redirectUrl, { replace: true });
  }, [navigate]);

  // Modified refreshAccessToken function for use within the AuthProvider
  // This uses the standalone function but also updates the React state
  const refreshToken = useCallback(async (): Promise<boolean> => {
    const result = await refreshAccessToken();
    
    // If refresh was successful, update the user state from localStorage
    if (result) {
      const userJson = localStorage.getItem('user');
      if (userJson) {
        setUser(JSON.parse(userJson));
        
        // Clear profile cache when token is refreshed
        profileCache.data = null;
        profileCache.timestamp = 0;
      }
    }
    
    return result;
  }, []);

  // Check for stored auth token on mount and handle OAuth callback
  useEffect(() => {
    const storedUser = localStorage.getItem('user');
    if (storedUser) {
      setUser(JSON.parse(storedUser));
    }

    // Handle OAuth callback
    const handleOAuthCallback = async () => {
      // Check if we're on the callback route
      if (location.pathname.includes('/auth/callback')) {
        const searchParams = new URLSearchParams(location.search);
        const access = searchParams.get('access');
        const refresh = searchParams.get('refresh');
        const error = searchParams.get('error');

        if (error) {
          console.error('OAuth error:', error);
          navigate('/');
          return;
        }

        if (access && refresh) {
          // We have tokens directly in the URL params
          console.log('Received tokens in URL params');
          handleTokens({ access, refresh });
          return;
        }

        // If no tokens in URL, try to get them from the token endpoint
        try {
          console.log('🔍 No tokens in URL, trying to get them from /auth/token/ endpoint...');
          const response = await fetch(`${API_URL}/auth/token/`, {
            method: 'GET',
            credentials: 'include'
          });

          if (response.ok) {
            const tokens = await response.json();
            console.log('✅ Successfully retrieved tokens from /auth/token/ endpoint');
            handleTokens(tokens);
          } else {
            console.error('❌ Failed to get tokens from /auth/token/ endpoint:', response.status);
            throw new Error('Failed to get tokens');
          }
        } catch (error) {
          console.error('Auth error:', error);
          navigate('/');
        }
      }
    };

    handleOAuthCallback();
  }, [location, navigate, handleTokens]);

  // Add event listener for auth:failed event
  useEffect(() => {
    const handleAuthFailed = (event: Event) => {
      const customEvent = event as CustomEvent;
      console.log('🔒 Auth failed event received:', customEvent.detail);
      
      // Clear user data
      localStorage.removeItem('user');
      setUser(null);
      
      // Clear profile cache
      profileCache.data = null;
      profileCache.timestamp = 0;
      
      // Redirect to home page
      navigate('/');
    };
    
    window.addEventListener('auth:failed', handleAuthFailed);
    
    return () => {
      window.removeEventListener('auth:failed', handleAuthFailed);
    };
  }, [navigate]);

  const login = useCallback(async (provider: 'google' | 'facebook' | 'microsoft') => {
    // Store the current URL to redirect back after auth
    const currentUrl = window.location.href;
    localStorage.setItem('auth_redirect', currentUrl);
    
    // First try to get tokens directly (in case we're already authenticated)
    try {
      console.log('🔍 Checking if already authenticated via /auth/token/ endpoint...');
      const response = await fetch(`${API_URL}/auth/token/`, {
        method: 'GET',
        credentials: 'include'
      });

      if (response.ok) {
        const tokens = await response.json();
        console.log('✅ Already authenticated, retrieved tokens from /auth/token/ endpoint');
        handleTokens(tokens);
        return;
      } else {
        console.log(`❌ Not already authenticated, status: ${response.status}`);
      }
    } catch (error) {
      console.log('Not already authenticated, starting OAuth flow...');
    }
    
    // If we couldn't get tokens directly, start the OAuth flow
    const frontendCallbackUrl = `${window.location.origin}/auth/callback`;
    
    // Use the new auth/login endpoint from the OpenAPI spec
    const loginUrl = `${API_URL.replace('/api', '')}/api/auth/login/${provider}/?redirect_uri=${encodeURIComponent(frontendCallbackUrl)}`;
    console.log('Redirecting to OAuth URL:', loginUrl);
    window.location.href = loginUrl;
  }, [handleTokens]);

  const logout = useCallback(async () => {
    // No need to call the server - tokens will expire naturally
    console.log('Logging out user - clearing local state');
    
    // Clear user data
    localStorage.removeItem('user');
    setUser(null);
    
    // Clear profile cache
    profileCache.data = null;
    profileCache.timestamp = 0;
    
    // Redirect to home
    navigate('/');
  }, [navigate]);

  const fetchUserProfile = useCallback(async (): Promise<User | null> => {
    try {
      // Check cache first - use cached data if it's less than 10 seconds old
      const now = Date.now();
      const CACHE_DURATION = 10 * 1000; // 10 seconds
      
      if (profileCache.data && now - profileCache.timestamp < CACHE_DURATION) {
        console.log('Using cached user profile data');
        return profileCache.data;
      }
      
      console.log('Fetching user profile from API');
      const userData = await api.get<User>('/auth/profile/');
      
      // Update cache
      profileCache.data = userData;
      profileCache.timestamp = now;
      
      // If we have a user in state with tokens, update the localStorage with the correct user data
      if (user && user.token && user.refresh_token) {
        const updatedUser = {
          ...userData,
          token: user.token,
          refresh_token: user.refresh_token,
        };
        
        localStorage.setItem('user', JSON.stringify(updatedUser));
        setUser(updatedUser);
      }
      
      return userData;
    } catch (error) {
      console.error('Error fetching user profile:', error);
      return null;
    }
  }, [user]);

  const value = {
    isAuthenticated: !!user,
    user,
    login,
    logout,
    handleTokens,
    refreshAccessToken: refreshToken,
    fetchUserProfile,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}; 